// src/components/auth/GoogleSignInButton.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';

const GoogleSignInButton = ({
  onSuccess,
  onError,
  buttonText = 'Continue with Google',
  disabled = false,
  redirectTo, // ✅ NEW
}) => {
  const buttonRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // Load Google Identity Services script
  useEffect(() => {
    if (window.google?.accounts?.id) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      console.error('Failed to load Google Identity Services');
      onError?.('Failed to load Google Sign-In. Please try again.');
    };
    document.body.appendChild(script);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [onError]);

  // Handle Google credential response
  const handleCredentialResponse = async (response) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      const result = await authService.googleLogin(response.credential);

      if (result.success) {
        setUser?.(result.user);
        onSuccess?.(result);

        // ✅ Redirect back to where the user came from (e.g. /checkout)
        navigate(redirectTo || '/account');
      } else {
        onError?.(result.message || 'Google sign-in failed');
        setGoogleLoading(false);
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      onError?.(err.message || 'Google sign-in failed');
      setGoogleLoading(false);
    }
  };

  // Initialize Google Sign-In
  useEffect(() => {
    if (!scriptLoaded || !window.google?.accounts?.id) return;

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID is not set');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
      use_fedcm_for_prompt: true,
    });

    if (buttonRef.current) {
      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 400,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded]);

  // Trigger the Google popup manually
  const handleCustomButtonClick = () => {
    if (!window.google?.accounts?.id) {
      onError?.('Google Sign-In is still loading. Please wait.');
      return;
    }

    setGoogleLoading(true);

    timeoutRef.current = setTimeout(() => {
      setGoogleLoading(false);
    }, 60000);

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        const hiddenBtn = buttonRef.current?.querySelector('div[role="button"]');
        if (hiddenBtn) {
          hiddenBtn.click();
        } else {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setGoogleLoading(false);
        }
      }
      if (notification.isDismissedMoment?.()) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setGoogleLoading(false);
      }
    });
  };

  return (
    <div className="relative">
      <div
        ref={buttonRef}
        className="absolute inset-0 opacity-0 pointer-events-none"
        aria-hidden="true"
      />

      <button
        type="button"
        onClick={handleCustomButtonClick}
        disabled={disabled || googleLoading || !scriptLoaded}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-[#DBDBDB] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {googleLoading ? (
          <>
            <Spinner />
            <span className="font-medium text-secondary text-base">
              Signing in with Google...
            </span>
          </>
        ) : (
          <>
            <GoogleIcon className="w-5 h-5" />
            <span className="font-medium text-secondary text-base">
              {buttonText}
            </span>
          </>
        )}
      </button>
    </div>
  );
};

/* ─────────── Inline components ─────────── */

const Spinner = () => (
  <svg
    className="animate-spin h-5 w-5 text-[#4285F4]"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const GoogleIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

export default GoogleSignInButton;